import { Component, ElementRef, ViewChild, OnInit, signal, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import * as d3 from 'd3';
import * as elkjs from 'elkjs/lib/elk.bundled.js';

// --- Interfaces ---

interface NetlistValue {
  magnitude: number[];
  units: { name: string; exponent?: number }[];
}

interface ComponentData {
  circuit: number;
  pos_pin: number;
  neg_pin: number;
  value?: NetlistValue;
  coil?: { turns_per_coil: number };
}

interface Netlist {
  [key: string]: ComponentData;
}

interface GraphNode {
  id: string;
  type: 'Resistor' | 'Inductor' | 'Capacitor' | 'Source' | 'FiniteElement' | 'Junction' | 'Reference';
  label: string;
  width: number;
  height: number;
  ports: { id: string; side?: 'WEST' | 'EAST' | 'NORTH' | 'SOUTH' }[];
  properties?: any;
}

interface GraphEdge {
  id: string;
  source: string;
  sourcePort: string;
  target: string;
  targetPort: string;
}

// ELK Interfaces
interface ElkNode {
  id: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  children?: ElkNode[];
  edges?: any[];
  labels?: { text: string }[];
  properties?: any;
}

// --- Constants ---

const SYMBOLS = {
  Resistor: {
    path: "M0,15 L10,15 L12.5,5 L17.5,25 L22.5,5 L27.5,25 L32.5,5 L37.5,25 L40,15 L60,15",
    width: 60,
    height: 30
  },
  Inductor: {
    path: "M0,15 L10,15 Q15,-5 20,15 Q25,-5 30,15 Q35,-5 40,15 Q45,-5 50,15 L60,15",
    width: 60,
    height: 30
  },
  Capacitor: {
    path: "M0,15 L25,15 M25,5 V25 M35,5 V25 M35,15 L60,15",
    width: 60,
    height: 30
  },
  FiniteElement: {
    width: 60,
    height: 30
  },
  Source: { width: 40, height: 40 },
  Junction: { width: 10, height: 10 },
  Reference: {
    // Triangle Ground
    path: "M10,0 L0,0 M0,0 L5,10 L10,0",
    width: 10,
    height: 10
  }
};

@Component({
  selector: 'app-netlist-viewer',
  standalone: true,
  imports: [],
  templateUrl: './netlist-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./netlist-viewer.component.css']
})
export class NetlistViewerComponent implements OnInit, OnChanges {
  @Input() netlistData: any;

  @ViewChild('svgContainer', { static: true }) svgContainer!: ElementRef;

  errorMessage = signal<string | null>(null);
  isProcessing = signal<boolean>(false);
  elkReady = signal<boolean>(false);

  private elkInstance: any = null;
  private svg: any = null;
  private rootGroup: any = null;
  private zoom: any = null;

  ngOnInit() {
    this.initD3();
    this.loadElk().then(() => {
      if (this.netlistData) {
        this.processNetlist();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['netlistData'] && this.elkReady()) {
      this.processNetlist();
    }
  }

  loadElk(): Promise<void> {
    try {
      this.elkInstance = new elkjs.default();
      this.elkReady.set(true);
      return Promise.resolve();
    } catch (err) {
      this.errorMessage.set("ELK failed to initialize");
      return Promise.reject(err);
    }
  }

  initD3() {
    const element = this.svgContainer.nativeElement;

    this.svg = d3.select(element).append("svg")
      .attr("width", "100%")
      .attr("height", "100%")
      .style("cursor", "grab");

    this.rootGroup = this.svg.append("g").attr("class", "zoom-layer");
    this.rootGroup.append("g").attr("class", "layer-edges");
    this.rootGroup.append("g").attr("class", "layer-nodes");

    // Shared defs for markers (arrow heads used for STEP_R visualization)
    const defs = this.svg.append("defs");
    defs
      .append("marker")
      .attr("id", "step-arrow")
      .attr("viewBox", "0 0 10 10")
      .attr("refX", "10")
      .attr("refY", "5")
      .attr("markerUnits", "strokeWidth")
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M 0 0 L 10 5 L 0 10 z")
      .attr("fill", "#ef4444");

    this.zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on("zoom", (event: any) => {
        this.rootGroup.attr("transform", event.transform);
      });

    this.svg.call(this.zoom);
  }

  async processNetlist() {
    if (!this.elkInstance || !this.netlistData) return;

    this.isProcessing.set(true);
    this.errorMessage.set(null);

    try {
      const { nodes, edges } = this.parseNetlist(this.netlistData);

      if (nodes.length === 0) {
        // Clear graph if empty
        this.render({ id: 'root', children: [], edges: [] });
        return;
      }

      const layout = await this.computeLayout(nodes, edges);
      this.render(layout);

    } catch (err: any) {
      console.error(err);
      this.errorMessage.set(err.message || "Unknown error processing netlist");
    } finally {
      this.isProcessing.set(false);
    }
  }

  parseNetlist(netlist: Netlist): { nodes: GraphNode[], edges: GraphEdge[] } {
    const nodes: GraphNode[] = [];
    const edges: GraphEdge[] = [];

    // Group by Circuit ID
    const circuits = new Map<number, { key: string, data: ComponentData }[]>();

    Object.entries(netlist).forEach(([key, comp]) => {
      const cId = comp.circuit || 1; // Default to 1
      if (!circuits.has(cId)) circuits.set(cId, []);
      circuits.get(cId)?.push({ key, data: comp });
    });

    // Process each circuit independently
    circuits.forEach((components, circuitId) => {
      const pinMap = new Map<number, Array<{ compId: string; type: 'pos' | 'neg' | 'p' }>>();
      const circuitPrefix = `C${circuitId}_`;

      // 1. Map Components
      components.forEach(({ key, data }) => {
        // Unify IDs with circuit prefix
        const uniqueId = `${circuitPrefix}${key}`;

        if (!pinMap.has(data.pos_pin)) pinMap.set(data.pos_pin, []);
        pinMap.get(data.pos_pin)?.push({ compId: uniqueId, type: 'pos' });

        if (!pinMap.has(data.neg_pin)) pinMap.set(data.neg_pin, []);
        pinMap.get(data.neg_pin)?.push({ compId: uniqueId, type: 'neg' });

        // Determine Component Type. Handle STEP_* prefixes (STEP_R, STEP_L, STEP_C)
        let type: GraphNode['type'] = 'Resistor';
        if (key.startsWith('STEP_')) {
          const p = key.split('_')[1] || '';
          if (p === 'I') type = 'Source';
          else if (p === 'L') type = 'Inductor';
          else if (p === 'C') type = 'Capacitor';
          else if (p === 'R') type = 'Resistor';
        } else {
          if (key.startsWith('I_')) type = 'Source';
          else if (key.startsWith('L_')) type = 'Inductor';
          else if (key.startsWith('C_')) type = 'Capacitor';
          else if (key.startsWith('slot_')) type = 'FiniteElement';
          else if (key.startsWith('R_')) type = 'Resistor';
        }
        if (data.coil) type = 'FiniteElement';

        const dims = SYMBOLS[type];
        nodes.push({
          id: uniqueId,
          type,
          label: key, // Show original name
          width: dims.width,
          height: dims.height,
          ports: [
            { id: `${uniqueId}_pos`, side: 'WEST' },
            { id: `${uniqueId}_neg`, side: 'EAST' }
          ],
          properties: { "elk.portConstraints": "FIXED_SIDE" }
        });
      });

      // 2. Process Nets (and Pin 1 Ground)
      pinMap.forEach((connections, pinId) => {
        // Always create a junction/net ID
        let jNodeId = `${circuitPrefix}Junction_p${pinId}`;
        let hasReference = false;

        // If Pin 1, force a Junction node to attach ground to
        // OR if > 2 connections, we need a junction anyway
        // Simple edge case (2 connections, not Pin 1) is optimized out usually,
        // but for Pin 1 we enforce a structure.

        if (pinId === 1) {
          // GROUND LOGIC
          // Create a dedicated Ground Reference Node
          const gndId = `${circuitPrefix}GND`;
          nodes.push({
            id: gndId,
            type: 'Reference',
            label: 'GND',
            width: SYMBOLS.Reference.width,
            height: SYMBOLS.Reference.height,
            ports: [{ id: `${gndId}_p`, side: 'NORTH' }], // Connects from top
            properties: { "elk.portConstraints": "FIXED_SIDE" }
          });

          // Add Ground to the connection list for this pin
          connections.push({ compId: gndId, type: 'p' as any });
          hasReference = true;
        }

        const connCount = connections.length;

        // Optimization: If only 2 components and NOT Pin 1, draw direct line
        if (connCount === 2 && !hasReference) {
          const [a, b] = connections;
          edges.push({
            id: `${circuitPrefix}edge_p${pinId}`,
            source: a.compId,
            sourcePort: `${a.compId}_${a.type}`,
            target: b.compId,
            targetPort: `${b.compId}_${b.type}`
          });
          return;
        }

        // --- Complex Net Logic (Star Point / Ground) ---

        // We reuse the previous splitting logic, but adapted for the prefix
        const sidePriority: ('WEST' | 'EAST' | 'SOUTH' | 'NORTH')[] = ['WEST', 'EAST', 'SOUTH', 'NORTH'];

        // If simple star (<=4), creates 1 junction. If complex (>4), creates chain.
        // Ground is just another connection now in `connections` array.

        if (connCount <= 4) {
          const ports: any[] = connections.map((_, i) => ({
            id: `${jNodeId}_p${i}`,
            side: sidePriority[i]
          }));

          nodes.push({
            id: jNodeId,
            type: 'Junction',
            label: `P${pinId}`,
            width: 10,
            height: 10,
            ports: ports,
            properties: { "elk.portConstraints": "FIXED_SIDE" }
          });

          connections.forEach((conn, idx) => {
            const targetPort = conn.type === 'p' ? `${conn.compId}_p` : `${conn.compId}_${conn.type}`;
            edges.push({
              id: `${circuitPrefix}edge_p${pinId}_${idx}`,
              source: jNodeId,
              sourcePort: `${jNodeId}_p${idx}`,
              target: conn.compId,
              targetPort: targetPort
            });
          });
        } else {
          // Split Logic
          // We treat Ground as just another connection here too

          const chunks: Array<typeof connections> = [];
          let remaining = [...connections];

          if (remaining.length > 0) chunks.push(remaining.splice(0, 3));
          while (remaining.length > 3) chunks.push(remaining.splice(0, 2));
          if (remaining.length > 0) chunks.push(remaining);

          chunks.forEach((chunk, i) => {
            const subJId = `${jNodeId}_${i}`;
            const isFirst = i === 0;
            const isLast = i === chunks.length - 1;

            const ports = [];
            if (!isFirst) ports.push({ id: `${subJId}_link_in`, side: 'WEST' });
            if (!isLast) ports.push({ id: `${subJId}_link_out`, side: 'EAST' });

            const availableSides: ('WEST' | 'EAST' | 'NORTH' | 'SOUTH')[] = ['NORTH', 'SOUTH'];
            if (isFirst) availableSides.unshift('WEST');
            if (isLast) availableSides.unshift('EAST');

            chunk.forEach((conn, ci) => {
              const side = availableSides[ci % availableSides.length];
              ports.push({ id: `${subJId}_ext_${ci}`, side: side });

              const targetPort = conn.type === 'p' ? `${conn.compId}_p` : `${conn.compId}_${conn.type}`;
              edges.push({
                id: `${circuitPrefix}edge_p${pinId}_${i}_ext_${ci}`,
                source: subJId,
                sourcePort: `${subJId}_ext_${ci}`,
                target: conn.compId,
                targetPort: targetPort
              });
            });

            if (!isLast) {
              const nextId = `${jNodeId}_${i + 1}`;
              edges.push({
                id: `${circuitPrefix}edge_p${pinId}_link_${i}`,
                source: subJId,
                sourcePort: `${subJId}_link_out`,
                target: nextId,
                targetPort: `${nextId}_link_in`
              });
            }

            nodes.push({
              id: subJId,
              type: 'Junction',
              label: `P${pinId}`,
              width: 10,
              height: 10,
              ports: ports as any,
              properties: { "elk.portConstraints": "FIXED_SIDE" }
            });
          });
        }
      });
    });

    return { nodes, edges };
  }

  async computeLayout(nodes: GraphNode[], edges: GraphEdge[]) {
    // We put all circuits in one big graph. ELK handles disjoint graphs fine.
    // They will be placed next to each other.
    const elkGraph = {
      id: "root",
      layoutOptions: {
        "elk.algorithm": "layered",
        "elk.direction": "RIGHT",
        "elk.spacing.nodeNode": "80.0",
        "elk.layered.spacing.edgeNodeBetweenLayers": "50.0",
        // Increase component spacing to separate disjoint circuits clearly
        "elk.spacing.componentComponent": "150.0",
        "elk.edgeRouting": "ORTHOGONAL",
        "elk.layered.crossingMinimization.strategy": "LAYER_SWEEP",
        "elk.layered.nodePlacement.bk.fixedAlignment": "BALANCED",
        "elk.layered.mergeEdges": "true"
      },
      children: nodes.map(n => {
        const ports = n.ports.map(p => {
          const pObj: any = { id: p.id, width: 0, height: 0 };
          if (p.side) pObj.properties = { "elk.port.side": p.side };
          return pObj;
        });
        return {
          id: n.id,
          width: n.width,
          height: n.height,
          labels: [{ text: n.label }],
          ports: ports,
          properties: { type: n.type, ...n.properties }
        };
      }),
      edges: edges.map(e => ({
        id: e.id,
        sources: [e.sourcePort],
        targets: [e.targetPort]
      }))
    };

    return await this.elkInstance.layout(elkGraph);
  }

  render(graphData: ElkNode) {
    const t = this.svg.transition().duration(750);

    // --- Nodes ---
    const nodes = graphData.children || [];
    const nodeSelection = this.rootGroup.select(".layer-nodes")
      .selectAll(".node")
      .data(nodes, (d: any) => d.id);

    const nodeEnter = nodeSelection.enter()
      .append("g")
      .attr("class", "node")
      .attr("transform", (d: any) => `translate(${d.x}, ${d.y})`)
      .style("opacity", 0);

    nodeEnter.each(function (this: any, d: any) {
      const type = d.properties?.type || 'Resistor';
      const g = d3.select(this);

      if (type === 'Junction') {
        g.append("circle").attr("cx", 5).attr("cy", 5).attr("r", 4).attr("fill", "#0f172a");
        if (!d.id.includes('_') || d.id.endsWith('_0')) {
          // Parse Pin ID from generated ID
          const pLabel = d.id.split('Junction_p')[1]?.split('_')[0] || '';
          if (pLabel !== '1') { // Don't label ground junctions to keep it clean
            g.append("text")
              .attr("x", 8).attr("y", -2)
              .style("font-family", "monospace").style("font-size", "8px").style("fill", "#94a3b8")
              .text(pLabel);
          }
        }
      } else if (type === 'Reference') {
        // Ground Symbol
        g.append("path")
          .attr("d", SYMBOLS.Reference.path)
          .attr("stroke", "#0f172a").attr("stroke-width", 2).attr("fill", "none");
      } else if (type === 'Source') {
        g.append("circle").attr("cx", 20).attr("cy", 20).attr("r", 15).attr("stroke", "#e11d48").attr("stroke-width", 2).attr("fill", "white");
        g.append("path").attr("d", "M20,10 V30 M15,15 L20,10 L25,15").attr("stroke", "#e11d48").attr("stroke-width", 2).attr("fill", "none");
        g.append("text")
          .attr("y", -8).attr("x", 20).attr("text-anchor", "middle")
          .style("font-family", "monospace").style("font-size", "10px").style("fill", "#64748b").style("font-weight", "bold")
          .text(d.labels?.[0]?.text);
      } else if (type === 'FiniteElement') {
        g.append("rect")
          .attr("x", 10).attr("y", 0).attr("width", 40).attr("height", 30)
          .attr("rx", 6)
          .attr("stroke", "#f97316").attr("stroke-width", 2).attr("fill", "white");
        g.append("line").attr("x1", 0).attr("y1", 15).attr("x2", 10).attr("y2", 15).attr("stroke", "#f97316").attr("stroke-width", 2);
        g.append("line").attr("x1", 50).attr("y1", 15).attr("x2", 60).attr("y2", 15).attr("stroke", "#f97316").attr("stroke-width", 2);
        g.append("circle").attr("cx", 16).attr("cy", 6).attr("r", 2.5).attr("fill", "#f97316");
        g.append("text")
          .attr("y", -8).attr("x", 30).attr("text-anchor", "middle")
          .style("font-family", "monospace").style("font-size", "10px").style("fill", "#64748b").style("font-weight", "bold")
          .text(d.labels?.[0]?.text);
      } else {
        // Resistor, Inductor, Capacitor
        const sym = SYMBOLS[type as 'Resistor' | 'Inductor' | 'Capacitor'];
        g.append("rect").attr("width", 60).attr("height", 30).attr("fill", "white").attr("opacity", 0); // Hit area
        g.append("path").attr("d", sym.path).attr("stroke", type === 'Inductor' ? "#f97316" : "#64748b").attr("stroke-width", 2).attr("stroke-linejoin", "round").attr("fill", "none");
        // If this component is a STEP_* (time-dependent) component for R/L/C,
        // draw a diagonal arrow/line across the symbol to indicate time dependency.
        const labelText = d.labels?.[0]?.text || '';
        const isStepTimeDep = labelText.startsWith('STEP_');
        const isPassiveType = type === 'Resistor' || type === 'Inductor' || type === 'Capacitor';
        if (isStepTimeDep && isPassiveType) {
          // Diagonal line with arrow pointing to bottom-right
          g.append("line")
            .attr("x1", 6)
            .attr("y1", 6)
            .attr("x2", 54)
            .attr("y2", 24)
            .attr("stroke", "#ef4444")
            .attr("stroke-width", 2)
            .attr("marker-end", "url(#step-arrow)");
        }
        g.append("text")
          .attr("y", -8).attr("x", 30).attr("text-anchor", "middle")
          .style("font-family", "monospace").style("font-size", "10px").style("fill", "#64748b").style("font-weight", "bold")
          .text(labelText);
      }
    });

    nodeSelection.merge(nodeEnter)
      .transition(t)
      .attr("transform", (d: any) => `translate(${d.x}, ${d.y})`)
      .style("opacity", 1);

    nodeSelection.exit().transition(t).style("opacity", 0).remove();

    // --- Edges ---
    const edges = graphData.edges || [];
    const lineGen = d3.line<{ x: number, y: number }>()
      .x((d: any) => d.x).y((d: any) => d.y).curve(d3.curveLinear);

    const getPath = (e: any) => {
      if (!e.sections?.[0]) return "";
      const s = e.sections[0];
      return lineGen([s.startPoint, ...(s.bendPoints || []), s.endPoint]) || "";
    };

    const edgeSelection = this.rootGroup.select(".layer-edges")
      .selectAll(".edge")
      .data(edges, (d: any) => d.id);

    const edgeEnter = edgeSelection.enter()
      .append("path")
      .attr("class", "edge")
      .attr("stroke", "#94a3b8")
      .attr("stroke-width", 2)
      .attr("fill", "none")
      .attr("d", (d: any) => getPath(d))
      .style("opacity", 0);

    edgeSelection.merge(edgeEnter)
      .transition(t)
      .attr("d", (d: any) => getPath(d))
      .style("opacity", 1);

    edgeSelection.exit().remove();
  }
}
